import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "overlay", "tab", "panel", "expandButton", "expandIcon", "collapseIcon"]
  static values = {
    isOpen: Boolean,
    currentTab: String,
    onboardingItems: Array,
    currentPath: String,
    isExpanded: Boolean,
    hasMatchingGuide: Boolean,
    matchingGuideSection: String
  }

  initialize() {
    // Initialize flag to prevent saving during initial load
    // This prevents the default HTML attribute value from overwriting saved state
    this._stateLoaded = false
    
    // Load state synchronously before Stimulus processes value change callbacks
    // This ensures the saved tab is restored before any callbacks fire
    this._loadStateSync()
  }

  connect() {
    // Ensure state is loaded (in case initialize didn't run or state wasn't found)
    if (!this._stateLoaded) {
      setTimeout(() => {
        this.loadState()
      }, 0)
    }
    
    this.loadExpandPreference()
    this.bindKeyEvents()
    this.bindOpenRequest()
  }

  disconnect() {
    this.unbindKeyEvents()
    this.unbindOpenRequest()
  }

  toggle() {
    if (!this.isOpenValue && this.hasMatchingGuideValue && this.matchingGuideSectionValue) {
      // If opening and there's a matching guide, switch to help tab and open it
      this.currentTabValue = 'help'
      this.isOpenValue = true
      // Wait for widget to open and help controller to connect
      this._waitForHelpControllerAndOpenGuide()
    } else {
      this.isOpenValue = !this.isOpenValue
    }
  }

  open() {
    if (this.hasMatchingGuideValue && this.matchingGuideSectionValue) {
      // If there's a matching guide, switch to help tab and open it
      this.currentTabValue = 'help'
      this.isOpenValue = true
      // Wait for widget to open and help controller to connect
      this._waitForHelpControllerAndOpenGuide()
    } else {
      this.isOpenValue = true
    }
  }

  _waitForHelpControllerAndOpenGuide() {
    // Wait for the help panel to be visible and controller to be ready
    const maxAttempts = 20
    let attempts = 0
    
    const tryOpenGuide = () => {
      attempts++
      
      // Find the help panel
      const helpPanel = this.panelTargets.find(panel => panel.dataset.tabName === 'help')
      if (!helpPanel) {
        if (attempts < maxAttempts) {
          setTimeout(tryOpenGuide, 50)
        }
        return
      }
      
      // Check if help controller is connected by looking for the controller element
      const helpControllerElement = helpPanel.querySelector('[data-controller*="floating-widget-help"]')
      if (!helpControllerElement) {
        if (attempts < maxAttempts) {
          setTimeout(tryOpenGuide, 50)
        }
        return
      }
      
      // Get the Stimulus controller instance
      const helpController = this.application.getControllerForElementAndIdentifier(
        helpControllerElement,
        'floating-widget-help'
      )
      
      if (helpController && helpController.guidesStructureValue && helpController.guidesStructureValue.length > 0) {
        // Controller is ready, open the guide
        this._openMatchingGuide()
      } else if (attempts < maxAttempts) {
        // Controller not ready yet, try again
        setTimeout(tryOpenGuide, 50)
      }
    }
    
    // Start checking after a short delay to allow DOM to update
    setTimeout(tryOpenGuide, 100)
  }

  _openMatchingGuide() {
    // Dispatch a custom event on document to open the guide
    const event = new CustomEvent('floating-widget:open-guide-section', {
      detail: { section: this.matchingGuideSectionValue },
      bubbles: true
    })
    
    document.dispatchEvent(event)
  }

  close() {
    this.isOpenValue = false
  }

  toggleExpand() {
    this.isExpandedValue = !this.isExpandedValue
    this.saveExpandPreference()
  }

  isOpenValueChanged() {
    if (this.isOpenValue) {
      this.overlayTarget.style.display = "block"
      this.modalTarget.style.display = "flex"
      
      // Block background scroll
      this._blockBackgroundScroll()
      
      // Apply the saved width preference
      this._updateModalWidth()
      
      // Add animation
      setTimeout(() => {
        this.overlayTarget.style.opacity = "1"
        this.modalTarget.style.transform = "scale(1)"
        this.modalTarget.style.opacity = "1"
        
        // Dispatch event that widget is opened (after animation completes)
        const event = new CustomEvent('floating-widget:opened', {
          detail: { currentTab: this.currentTabValue },
          bubbles: true
        })
        document.dispatchEvent(event)
      }, 10)
    } else {
      // Start closing animation
      this.overlayTarget.style.opacity = "0"
      this.modalTarget.style.transform = "scale(0.95)"
      this.modalTarget.style.opacity = "0"
      
      // Hide completely after animation
      setTimeout(() => {
        this.overlayTarget.style.display = "none"
        this.modalTarget.style.display = "none"
        // Restore background scroll
        this._restoreBackgroundScroll()
      }, 200)
    }
    
    this.saveState()
  }

  switchTab(event) {
    const tabName = event.currentTarget.dataset.tabName
    this.currentTabValue = tabName
  }

  currentTabValueChanged() {
    // Check if this is the default "discover" value and we have a saved tab to restore
    if (this.currentTabValue === 'discover' && !this._stateLoaded) {
      const stored = localStorage.getItem("floating-widget-progress")
      if (stored) {
        try {
          const data = JSON.parse(stored)
          if (data.currentTab && data.currentTab !== 'discover') {
            // Restore the saved tab instead of using default
            this._stateLoaded = false
            this.currentTabValue = data.currentTab
            setTimeout(() => {
              this._stateLoaded = true
            }, 10)
            return // Exit early, will be called again with restored value
          }
        } catch (e) {
          // Continue with normal flow if parsing fails
        }
      }
    }

    // Safety check: only proceed if targets are available
    try {
      if (!this.tabTargets || this.tabTargets.length === 0) return
      if (!this.panelTargets || this.panelTargets.length === 0) return
    } catch (e) {
      return
    }

    // Update tab buttons
    this.tabTargets.forEach(tab => {
      const isActive = tab.dataset.tabName === this.currentTabValue
      
      if (isActive) {
        tab.classList.remove("border-transparent", "text-gray-600", "hover:text-gray-900", "hover:bg-gray-100")
        tab.classList.add("border-gobpink", "text-gobpink", "bg-white")
        tab.setAttribute("aria-selected", "true")
      } else {
        tab.classList.add("border-transparent", "text-gray-600", "hover:text-gray-900", "hover:bg-gray-100")
        tab.classList.remove("border-gobpink", "text-gobpink", "bg-white")
        tab.setAttribute("aria-selected", "false")
      }
    })

    // Update panels with fade animations
    // Only animate if state is loaded (user-initiated change, not initial load)
    const shouldAnimate = this._stateLoaded
    
    // Find the currently visible panel (before we start changing things)
    const currentlyVisiblePanel = this.panelTargets.find(p => 
      !p.classList.contains("hidden")
    )
    
    this.panelTargets.forEach(panel => {
      const isActive = panel.dataset.tabName === this.currentTabValue
      
      if (isActive) {
        // This is the panel we want to show
        if (currentlyVisiblePanel && currentlyVisiblePanel !== panel && shouldAnimate) {
          // Fade out the current panel, then fade in the new one
          this._fadeOut(currentlyVisiblePanel, () => {
            currentlyVisiblePanel.classList.add("hidden")
            currentlyVisiblePanel.classList.remove("fade-out", "fade-in")
            
            // Show and fade in the new panel
            panel.classList.remove("hidden")
            this._fadeIn(panel)
            
            // If switching to help tab, trigger restore of last viewed guide
            if (panel.dataset.tabName === 'help') {
              setTimeout(() => {
                const event = new CustomEvent('floating-widget:tab-changed', {
                  detail: { tab: 'help' },
                  bubbles: true
                })
                document.dispatchEvent(event)
              }, 100)
            }
          })
        } else {
          // No panel was visible, or it's the same panel, or initial load - just show the panel
          // But first, hide any currently visible panel if it's different
          if (currentlyVisiblePanel && currentlyVisiblePanel !== panel) {
            currentlyVisiblePanel.classList.add("hidden")
            currentlyVisiblePanel.classList.remove("fade-out", "fade-in")
          }
          
          panel.classList.remove("hidden")
          if (shouldAnimate && currentlyVisiblePanel !== panel) {
            this._fadeIn(panel)
          } else {
            panel.classList.remove("fade-out", "fade-in")
          }
          
          // If switching to help tab, trigger restore of last viewed guide
          if (panel.dataset.tabName === 'help') {
            setTimeout(() => {
              const event = new CustomEvent('floating-widget:tab-changed', {
                detail: { tab: 'help' },
                bubbles: true
              })
              document.dispatchEvent(event)
            }, 100)
          }
        }
      } else {
        // Panel is not active - ensure it's hidden
        if (!panel.classList.contains("hidden")) {
          panel.classList.add("hidden")
          panel.classList.remove("fade-out", "fade-in")
        }
      }
    })

    // Only save state if widget is initialized (avoid saving during initial load)
    // Check if this is a user-initiated change by checking if we've loaded state
    if (this._stateLoaded) {
      this.saveState()
    }
  }


  getCompletedItems() {
    const stored = localStorage.getItem("floating-widget-progress")
    if (!stored) return []
    
    try {
      const data = JSON.parse(stored)
      return data.completedItems || []
    } catch (e) {
      return []
    }
  }

  saveCompletedItems(completedItems) {
    const data = {
      completedItems: completedItems,
      currentTab: this.currentTabValue
    }
    localStorage.setItem("floating-widget-progress", JSON.stringify(data))
  }

  _loadStateSync() {
    // Synchronous version for initialize() - loads state before value callbacks fire
    const stored = localStorage.getItem("floating-widget-progress")
    if (!stored) {
      this._stateLoaded = true
      return
    }
    
    try {
      const data = JSON.parse(stored)
      if (data.currentTab && data.currentTab !== 'discover') {
        // Set flag before changing value to prevent saving during load
        this._stateLoaded = false
        // Set the value directly - this will trigger the callback but _stateLoaded=false prevents saving
        // We need to access the value property directly before Stimulus initializes it
        const currentValue = this.element.getAttribute('data-floating-widget-current-tab-value')
        if (currentValue === 'discover') {
          // Only override if it's still the default value
          this.element.setAttribute('data-floating-widget-current-tab-value', data.currentTab)
        }
        // Mark state as loaded after Stimulus processes the attribute change
        setTimeout(() => {
          this._stateLoaded = true
        }, 10)
      } else {
        this._stateLoaded = true
      }
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error loading floating widget state:", e)
      }
      this._stateLoaded = true
    }
  }

  loadState() {
    const stored = localStorage.getItem("floating-widget-progress")
    if (!stored) {
      this._stateLoaded = true
      return
    }
    
    try {
      const data = JSON.parse(stored)
      if (data.currentTab) {
        // Set flag before changing value to prevent saving during load
        this._stateLoaded = false
        this.currentTabValue = data.currentTab
        // Mark state as loaded after a brief delay to allow change callback to complete
        setTimeout(() => {
          this._stateLoaded = true
        }, 50)
      } else {
        this._stateLoaded = true
      }
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error loading floating widget state:", e)
      }
      this._stateLoaded = true
    }
  }

  saveState() {
    const completedItems = this.getCompletedItems()
    this.saveCompletedItems(completedItems)
  }

  // Open assistant (called from contact tab)
  openAssistant() {
    this.close()
    // Trigger the floating assistant
    const floatingAssistant = document.querySelector('[data-controller*="floating-assistant"]')
    if (floatingAssistant) {
      const controller = this.application.getControllerForElementAndIdentifier(
        floatingAssistant,
        "floating-assistant"
      )
      if (controller && controller.open) {
        controller.open()
      }
    }
  }

  // Open Crisp chat (called from contact tab)
  openChat() {
    this.close()
    
    // Initialize window.$crisp if it doesn't exist
    if (!window.$crisp) {
      window.$crisp = []
    }
    
    // Show the chat widget first (it's hidden by default via chat:hide)
    // Then open it - matching the pattern from assistant_feedback_controller.js
    window.$crisp.push(["do", "chat:show"])
    window.$crisp.push(["do", "chat:open"])
  }

  bindKeyEvents() {
    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.handleKeydown)
  }

  unbindKeyEvents() {
    document.removeEventListener('keydown', this.handleKeydown)
  }

  handleKeydown(event) {
    // Ignore keyboard shortcuts when user is typing in input fields
    const target = event.target
    const isInputField = target.tagName === 'INPUT' || 
                         target.tagName === 'TEXTAREA' || 
                         target.isContentEditable
    
    if (isInputField) {
      return
    }
    
    if (event.key === 'Escape' && this.isOpenValue) {
      this.close()
    } else if (event.key === '?' && event.shiftKey && !this.isOpenValue) {
      this.open()
    }
  }

  bindOpenRequest() {
    this.handleOpenRequest = this.handleOpenRequest.bind(this)
    document.addEventListener('floating-widget:open-request', this.handleOpenRequest)
  }

  unbindOpenRequest() {
    document.removeEventListener('floating-widget:open-request', this.handleOpenRequest)
  }

  handleOpenRequest(event) {
    event.preventDefault()
    this.open()
  }

  isExpandedValueChanged() {
    // Safety check: only proceed if targets are available
    try {
      if (!this.expandIconTarget || !this.collapseIconTarget) return
    } catch (e) {
      return
    }
    
    // Apply changes immediately (for when modal is not yet visible)
    this._updateModalWidth()
  }

  _updateModalWidth() {
    // Always update icons regardless of modal visibility
    if (this.isExpandedValue) {
      // Show collapse icon, hide expand icon
      this.expandIconTarget.classList.add('hidden')
      this.collapseIconTarget.classList.remove('hidden')
    } else {
      // Show expand icon, hide collapse icon
      this.expandIconTarget.classList.remove('hidden')
      this.collapseIconTarget.classList.add('hidden')
    }

    // Only update width if modal is visible
    if (this.modalTarget.style.display === 'flex') {
      if (this.isExpandedValue) {
        // Expanded: 90% width
        this.modalTarget.classList.remove('w-[50vw]')
        this.modalTarget.classList.add('w-[90vw]')
      } else {
        // Collapsed: 50% width
        this.modalTarget.classList.remove('w-[90vw]')
        this.modalTarget.classList.add('w-[50vw]')
      }
    }
  }

  loadExpandPreference() {
    const saved = localStorage.getItem('floating-widget-expanded')
    if (saved !== null) {
      this.isExpandedValue = saved === 'true'
    }
  }

  saveExpandPreference() {
    localStorage.setItem('floating-widget-expanded', this.isExpandedValue.toString())
  }

  _blockBackgroundScroll() {
    // Store the current scroll position
    this._scrollPosition = window.pageYOffset || document.documentElement.scrollTop
    
    // Add styles to prevent scrolling
    document.body.style.position = 'fixed'
    document.body.style.top = `-${this._scrollPosition}px`
    document.body.style.width = '100%'
    document.body.style.overflow = 'hidden'
  }

  _restoreBackgroundScroll() {
    // Remove the fixed positioning styles
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.width = ''
    document.body.style.overflow = ''
    
    // Restore the scroll position
    if (this._scrollPosition !== undefined) {
      window.scrollTo(0, this._scrollPosition)
      this._scrollPosition = undefined
    }
  }

  /**
   * Fade out an element
   * @param {HTMLElement} element - Element to fade out
   * @param {Function} callback - Callback to execute after fade out completes
   */
  _fadeOut(element, callback) {
    if (!element) {
      if (callback) callback()
      return
    }
    
    element.classList.remove('fade-in')
    element.classList.add('fade-out')
    
    setTimeout(() => {
      if (callback) callback()
    }, 200) // Match CSS transition duration
  }

  /**
   * Fade in an element
   * @param {HTMLElement} element - Element to fade in
   */
  _fadeIn(element) {
    if (!element) return
    
    // Start from opacity 0 to ensure smooth fade in
    element.style.opacity = '0'
    element.classList.remove('fade-out')
    
    // Force reflow to ensure transition works
    void element.offsetHeight
    
    // Remove inline opacity and add fade-in class to trigger animation
    element.style.opacity = ''
    element.classList.add('fade-in')
  }
}

